import { Transform } from "../../../transform";

import "./spread-renderer.css";

export interface ISpreadRenderer {
	/** element representing a spread image */
	el: HTMLElement;

	/** Prepare for rendering spread */
	getRenderResourcesAsync(spread: eXpress.core.Spread): Promise<eXpress.IDetectLastResult<void>>;

	/**
	 * Start a rerender of the spread
	 * First call getRenderResourcesAsync
	 * Accepts a eXpress.IDetectLastResult, because getRenderResourcesAsync already prepared spreadSurface for that spread
	 * and it cannot be used for another spread.
	 */
	renderSpread(
		getSpreadResourcesResult: eXpress.IDetectLastResult<void>,
		renderPreviewMode: boolean,
		adornerOptions: { opacity?: number },
		topZone?: eXpress.core.IZone,
	): void;

	/** Get the size of the rendered spread, when not yet rendered, pass the spread that will be rendered */
	getRenderSize(spread?: eXpress.math.IVector): eXpress.math.IVector;

	getRenderToSpreadTransform(spread?: eXpress.math.IVector): Transform;

	getZonesAtPoint(
		pointInSpreadSurfaceSpace: eXpress.math.IPoint,
		spread?: eXpress.core.Spread,
	): eXpress.core.IZone[];

	applyTransform(transform: Transform): void;

	readonly isDisposed: boolean;
	dispose(): void;
}

interface ISpreadRendererOptions {
	maxRenderSizeInPx?: number;
	renderingLoadingText?: string;
	showDebugLayers?: boolean;
	/** Show render Effects (gold foil, noise, bevel, ...) */
	renderEffects?: boolean;
	useSvgLayer?: boolean;
}

export interface ISpreadRendererDeps {
	logger: eXpress.ILogger;
	editor: eXpress.core.IEditor;
	localizedContent: eXpress.creator.LocalizedContent;
	fontFamilyLoader: eXpress.creator.rendering.FontFamilyLoader;
	defaultFontFamily: eXpress.core.FontFamily;
	smartFilterLoader?: eXpress.creator.rendering.SmartFilterLoader | undefined;
}

export function createSpreadRenderer(
	options: ISpreadRendererOptions,
	deps: ISpreadRendererDeps,
): ISpreadRenderer {
	// Collect info from arguments
	const {
		maxRenderSizeInPx = 750,
		renderingLoadingText = "Loading...",
		showDebugLayers = false,
		useSvgLayer = false,
		renderEffects = false,
	} = options;
	const {
		editor,
		localizedContent,
		fontFamilyLoader,
		defaultFontFamily,
		logger,
		smartFilterLoader,
	} = deps;

	const {
		creator: {
			rendering: { CanvasGraphicsLayer, SvgTextLayer, SpreadSurface },
		},
	} = eXpress;

	let lastRenderedSpread: eXpress.core.Spread | undefined;
	// let lastRenderPreviewMode = false;

	const el = document.createElement("div");
	el.style.overflow = "hidden"; // Inner canvas can be larger then div
	el.style.transformOrigin = "0 0";
	el.classList.add("spread");

	const spreadSurface = new SpreadSurface(editor, localizedContent, smartFilterLoader, el);

	// We currently create a new EffectCache for each spread.
	// Reason: We don't have logic yet to cleanup unused effects in a shared EffectCache
	const effectCache =
		new eXpress.creator.rendering.EffectCache<eXpress.creator.rendering.MipMapCache>(
			(image, key) =>
				new eXpress.creator.rendering.MipMapCache(
					image,
					eXpress.platform.canvas,
					logger.error.bind(logger),
					key,
				),
		);

	// Create the Image/(Text) Layer
	const canvasLayer = new CanvasGraphicsLayer(
		spreadSurface,
		fontFamilyLoader,
		renderingLoadingText,
		() => defaultFontFamily,
		logger,
		effectCache,
		editor.client,
		spreadSurface.smartFilterFunction,
		eXpress.math.vector(maxRenderSizeInPx),
		eXpress.platform.canvas,
		eXpress.platform.image,
		{
			filter: useSvgLayer
				? eXpress.creator.rendering.ZoneFilter.Img
				: eXpress.creator.rendering.ZoneFilter.All,
			textRenderMode: eXpress.creator.rendering.TextRenderMode.Exact,
			coarseImageLod: eXpress.core.ImageLod.Medium,
			detailImageLod: eXpress.core.ImageLod.Large,
			renderEffects,
		},
	);
	spreadSurface.layers.push(canvasLayer);
	canvasLayer.element.classList.add("spread-renderer__canvas");
	el.appendChild(canvasLayer.element);

	// Create the Text Layer
	let svgLayer: eXpress.creator.rendering.SvgTextLayer | undefined;
	if (useSvgLayer) {
		svgLayer = new SvgTextLayer(
			spreadSurface,
			editor.logger,
			() => defaultFontFamily,
			fontFamilyLoader,
			{
				renderEmptyTextZones: true,
				renderEffects,
			},
		);
		spreadSurface.layers.push(svgLayer);
		svgLayer.element.classList.add("spread-renderer__svg");
		el.appendChild(svgLayer.element);
	}

	// Add a Debug layer
	if (showDebugLayers) {
		const debugLayer = new eXpress.creator.rendering.DebugLayer(spreadSurface);
		spreadSurface.layers.push(debugLayer);
		debugLayer.element.style.transformOrigin = "0 0";
		el.appendChild(debugLayer.element);
	}

	/** Make it detectable if it is the last loadSpread requested */
	const loadResourcesAsync = eXpress.detectLastAsync(
		(spread: eXpress.core.Spread) =>
			spreadSurface.loadResourcesAsync(spread).requiredResourcesPromise,
	);

	// This function is used to load Spread rendering resources
	function getRenderResourcesAsync(spread: eXpress.core.Spread) {
		// Remember last rendered spread so we can refresh it if props change
		lastRenderedSpread = spread;

		return loadResourcesAsync(spread);
	}

	// This function can be used to render a spread
	function renderSpread(
		getSpreadResourcesResult: eXpress.IDetectLastResult<void>,
		renderPreviewMode: boolean,
		adornerOptions: { opacity?: number },
		topZone?: eXpress.core.IZone,
	) {
		// Because reloadAsync sets the spread to render, we check here
		// the resources for that spread are fetched and ready
		const { getIsLastRequested } = getSpreadResourcesResult || {};
		if (!getIsLastRequested()) return; // Don't render previous request

		// Remember if preview mode, in case props change and we re-create the renderer
		// lastRenderPreviewMode = renderPreviewMode;

		el.style.opacity = (adornerOptions.opacity ?? 1).toString();
		spreadSurface.setPreviewMode(renderPreviewMode);
		spreadSurface.resize(
			eXpress.math.vector(maxRenderSizeInPx),
			false,
			eXpress.math.rect(0, 0, maxRenderSizeInPx, maxRenderSizeInPx),
		);
		spreadSurface.draw(topZone);
	}

	/** Size of the spread (is not the image size) */
	function getRenderSize(spread: eXpress.math.IVector | undefined = lastRenderedSpread) {
		return spread
			? eXpress.math.vector(
					eXpress.math
						.rect(spread)
						.fit(eXpress.math.rect(0, 0, maxRenderSizeInPx, maxRenderSizeInPx)),
			  )
			: eXpress.math.Rect.zero;
	}

	/** Get the transform to go from the rendered image to spread DIP's */
	function getRenderToSpreadTransform(
		spread: eXpress.math.IVector | undefined = lastRenderedSpread,
	) {
		if (!spread) return Transform.unit;
		return new Transform({
			scale: spread.width / getRenderSize(spread).width,
		});
	}

	function getZonesAtPoint(
		pointInSpreadDip: eXpress.math.IPoint,
		spread: eXpress.core.Spread | undefined = lastRenderedSpread,
	) {
		if (!spread || !spread.hasOwner() || !eXpress.math.hasSize(spread)) return [];
		return spread.hitTest(pointInSpreadDip);
	}

	/** Apply transform on spread render layers with a transform */
	function applyTransform(transform: Transform) {
		// We currently perform a scale on all layers (debugLayer could be resize)
		transform.setCssTransform(el);

		// Resize the SVG to the actual display size and counter-scale its container to neutralize el's scale.
		// This avoids the GPU compositor upscaling the SVG rasterization, which causes blurry text on Mac/HiDPI.
		if (svgLayer && transform.scale > 0 && eXpress.math.hasSize(spreadSurface.size)) {
			const renderSize = spreadSurface.size;
			svgLayer.setDisplaySize(
				eXpress.math.vector(
					renderSize.width * transform.scale,
					renderSize.height * transform.scale,
				),
			);
		}
	}

	let isDisposed = false;
	function dispose() {
		isDisposed = true;
		// Dispose used layers
		spreadSurface.layers.forEach((layer) => {
			if (eXpress.utils.isDisposable(layer)) {
				eXpress.utils.dispose(layer);
			}
		});

		spreadSurface.dispose();
		effectCache.dispose(false); // not a shared cache, dispose all elements
	}

	return {
		el,
		getRenderResourcesAsync,
		renderSpread,
		getRenderSize,
		getRenderToSpreadTransform,
		getZonesAtPoint,
		applyTransform,
		dispose,
		get isDisposed() {
			return isDisposed;
		},
	};
}
